import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  Check,
  Copy,
  Download,
  FileUp,
  Loader2,
  Lock,
  MessageSquare,
  Paperclip,
  Pencil,
  Plus,
  QrCode,
  RefreshCw,
  Settings,
  Trash2,
  Unlock,
  Upload,
  Users,
  X,
} from 'lucide-react';
import QRCode from 'qrcode';

import {
  TASK_FILE_ACCEPT,
  type Course,
  type TaskDetail,
  type TaskRosterRow,
  type TaskSubmissionSummary,
  type TaskSummary,
} from '../../shared/tasks';
import { api, type Profile } from '../lib/api';
import {
  checkTaskFile,
  formatBytes,
  formatDue,
  nameFromFile,
  nameKey,
  openFile,
  taskApi,
  taskUrl,
} from '../lib/tasks';
import { useSubPath } from '../lib/useSubPath';
import { formatGrade } from './ExamTake';
import { Modal, SmallButton, Toggle, inputClass } from './ExamsView';

/**
 * Mis tareas: como en un aula virtual, ordenadas por curso. Quien la crea deja
 * instrucciones (y, si quiere, un archivo) y comparte el enlace o su QR; cada
 * estudiante sube su trabajo en PDF o Word, sin cuenta, y aquí se descarga y
 * se califica.
 *
 * En un curso con lista, cada estudiante elige su nombre al entrar y puede
 * tener su propia práctica para descargar: se suben todas de golpe y se
 * reparten solas por el nombre del archivo.
 */
export const TasksView: React.FC<{
  profile: Profile | null;
  /** Cambia cada vez que se pide «Crear tarea» desde la barra: abre el formulario. */
  createRequest?: number;
}> = ({ profile, createRequest = 0 }) => {
  const [tasks, setTasks] = useState<TaskSummary[] | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [managing, setManaging] = useState(false);
  /** Curso que se está mirando: id, '' para «sin curso», null para todas. */
  const [filter, setFilter] = useState<string | null>(null);
  // La tarea abierta va en la dirección (/mis-tareas/<id>).
  const [[openId], setSub] = useSubPath('/mis-tareas');
  const setOpenId = (id: string | null, opts?: { replace?: boolean }) => setSub(id ? [id] : [], opts);

  const load = useCallback(async () => {
    if (!profile) return;
    try {
      const [t, c] = await Promise.all([
        api.get<{ tasks: TaskSummary[] }>('/tasks/mine'),
        api.get<{ courses: Course[] }>('/courses'),
      ]);
      setTasks(t.tasks);
      setCourses(c.courses);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron cargar tus tareas.');
    }
  }, [profile]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (createRequest > 0 && profile) {
      setOpenId(null);
      setCreating(true);
    }
  }, [createRequest, profile]);

  // Una sección por curso, en el orden de los cursos; las sueltas al final.
  const groups = useMemo(() => {
    if (!tasks) return [];
    const out: { key: string; title: string; tasks: TaskSummary[] }[] = courses.map((c) => ({
      key: c.id,
      title: c.name,
      tasks: tasks.filter((t) => t.courseId === c.id),
    }));
    out.push({ key: '', title: 'Sin curso', tasks: tasks.filter((t) => !t.courseId) });
    return out.filter((g) => (filter === null ? g.tasks.length > 0 : g.key === filter));
  }, [tasks, courses, filter]);

  if (openId) {
    return (
      <TaskDetailView
        taskId={openId}
        courses={courses}
        onCoursesChange={() => void load()}
        onBack={(opts) => {
          setOpenId(null, opts);
          void load();
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <section className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold">Mis tareas</h2>
            <p className="text-ink-400 text-sm">
              Deja las instrucciones y comparte el enlace o su código QR: solo con él se puede entrar. Cada estudiante
              sube su trabajo en PDF o Word, sin cuenta, y tú lo calificas aquí.
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <button
              onClick={() => setManaging(true)}
              className="flex items-center gap-1.5 px-4 h-10 rounded-lg font-semibold text-sm whitespace-nowrap border border-ink-700 bg-ink-850 hover:bg-ink-800 text-ink-100"
            >
              <Users className="w-4 h-4" /> Cursos
            </button>
            <button
              onClick={() => setCreating(true)}
              className="flex items-center gap-1.5 px-4 h-10 rounded-lg font-semibold text-sm whitespace-nowrap bg-magenta-600 hover:bg-magenta-500 text-white"
            >
              <Plus className="w-4 h-4" /> Nueva tarea
            </button>
          </div>
        </div>

        {courses.length > 0 && (
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Curso">
            <Chip active={filter === null} onClick={() => setFilter(null)}>
              Todas
            </Chip>
            {courses.map((c) => (
              <Chip key={c.id} active={filter === c.id} onClick={() => setFilter(c.id)}>
                {c.name}
              </Chip>
            ))}
            <Chip active={filter === ''} onClick={() => setFilter('')}>
              Sin curso
            </Chip>
          </div>
        )}

        {error && <p className="text-danger-400 text-sm">{error}</p>}
        {!tasks && !error && <Loader2 className="w-6 h-6 animate-spin text-ink-400" />}
        {tasks && groups.every((g) => g.tasks.length === 0) && (
          <p className="surface p-6 text-ink-400 text-sm text-center">
            {filter === null ? 'Aún no has dejado ninguna tarea.' : 'No hay tareas en este curso.'}
          </p>
        )}

        {groups
          .filter((g) => g.tasks.length > 0)
          .map((g) => (
            <div key={g.key || 'sin-curso'} className="space-y-2">
              {(courses.length > 0 || g.key) && (
                <h3 className="text-sm font-bold uppercase tracking-wider text-ink-400">{g.title}</h3>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                {g.tasks.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setOpenId(t.id)}
                    className="surface p-4 text-left hover:border-magenta-500/60 transition-colors space-y-2"
                  >
                    <div className="flex items-start gap-2">
                      <p className="font-bold flex-1 min-w-0 truncate">{t.title}</p>
                      <span
                        className={`chip ${t.isOpen ? 'border-success-500/50 text-success-400' : 'border-ink-600 text-ink-400'}`}
                      >
                        {t.isOpen ? 'Abierta' : 'Cerrada'}
                      </span>
                    </div>
                    <p className="text-ink-400 text-xs">
                      {t.dueAt ? `Entrega: ${formatDue(t.dueAt)} · ` : 'Sin fecha de entrega · '}
                      {t.submitted} {t.submitted === 1 ? 'entrega' : 'entregas'}
                      {t.submitted > 0 ? ` (${t.graded} calificada${t.graded === 1 ? '' : 's'})` : ''}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          ))}
      </section>

      {creating && (
        <TaskFormModal
          courses={courses}
          defaultCourseId={filter || null}
          onManageCourses={() => setManaging(true)}
          onClose={() => setCreating(false)}
          onSaved={(task) => {
            setCreating(false);
            setOpenId(task.id);
          }}
        />
      )}

      {managing && (
        <CoursesModal
          courses={courses}
          onChange={(next) => setCourses(next)}
          onClose={() => {
            setManaging(false);
            void load();
          }}
        />
      )}
    </div>
  );
};

const Chip: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({
  active,
  onClick,
  children,
}) => (
  <button
    role="tab"
    aria-selected={active}
    onClick={onClick}
    className={`px-3 h-8 rounded-full text-sm font-semibold border transition-colors ${
      active ? 'border-magenta-500 bg-magenta-500/15 text-ink-50' : 'border-ink-700 text-ink-400 hover:text-ink-100'
    }`}
  >
    {children}
  </button>
);

/* ================================================================
 * Cursos
 * ================================================================ */

/** Un nombre por línea; se ignoran líneas vacías. */
const parseNames = (text: string) =>
  text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

const CoursesModal: React.FC<{
  courses: Course[];
  onChange: (courses: Course[]) => void;
  onClose: () => void;
}> = ({ courses, onChange, onClose }) => {
  const [openId, setOpenId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [names, setNames] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const open = courses.find((c) => c.id === openId) ?? null;

  const replace = (c: Course) => onChange(courses.map((x) => (x.id === c.id ? c : x)));

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar.');
    } finally {
      setBusy(false);
    }
  };

  const create = () =>
    run(async () => {
      const { course } = await api.post<{ course: Course }>('/courses', { name, students: parseNames(names) });
      onChange([...courses, course].sort((a, b) => a.name.localeCompare(b.name, 'es')));
      setName('');
      setNames('');
      setOpenId(course.id);
    });

  if (open) {
    return (
      <Modal title={`Curso ${open.name}`} onClose={onClose} wide>
        <CourseEditor
          course={open}
          busy={busy}
          error={error}
          onBack={() => {
            setOpenId(null);
            setError('');
          }}
          onRename={(n) =>
            run(async () => replace((await api.patch<{ course: Course }>(`/courses/${open.id}`, { name: n })).course))
          }
          onAdd={(list) =>
            run(async () =>
              replace((await api.post<{ course: Course }>(`/courses/${open.id}/students`, { names: list })).course),
            )
          }
          onRemove={(studentId, studentName) => {
            if (!confirm(`¿Quitar a ${studentName} del curso? Se borrará su práctica en las tareas del curso.`)) return;
            void run(async () =>
              replace((await api.delete<{ course: Course }>(`/courses/${open.id}/students/${studentId}`)).course),
            );
          }}
          onRenameStudent={(studentId, n) =>
            run(async () =>
              replace((await api.patch<{ course: Course }>(`/courses/${open.id}/students/${studentId}`, { name: n })).course),
            )
          }
          onDelete={() => {
            if (!confirm(`¿Borrar el curso ${open.name}? Sus tareas no se borran: se quedan sin curso.`)) return;
            void run(async () => {
              await api.delete(`/courses/${open.id}`);
              onChange(courses.filter((c) => c.id !== open.id));
              setOpenId(null);
            });
          }}
        />
      </Modal>
    );
  }

  return (
    <Modal title="Cursos" onClose={onClose} wide>
      <div className="space-y-5">
        {courses.length === 0 ? (
          <p className="text-ink-400 text-sm">
            Aún no tienes cursos. Crea uno (por ejemplo «4to») para ordenar sus tareas y, si quieres, con la lista de
            estudiantes: así cada uno elige su nombre al entrar y descarga su propia práctica.
          </p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            {courses.map((c) => (
              <button
                key={c.id}
                onClick={() => setOpenId(c.id)}
                className="flex items-center gap-3 px-3 py-3 rounded-lg border border-ink-700 bg-ink-850 hover:bg-ink-800 text-left"
              >
                <Users className="w-4 h-4 text-magenta-400 shrink-0" />
                <span className="flex-1 min-w-0">
                  <span className="block font-semibold truncate">{c.name}</span>
                  <span className="block text-ink-500 text-xs">
                    {c.students.length ? `${c.students.length} estudiantes` : 'Sin lista'}
                  </span>
                </span>
                <Pencil className="w-4 h-4 text-ink-500" />
              </button>
            ))}
          </div>
        )}

        <div className="space-y-3 rounded-xl border border-ink-700 p-4">
          <p className="font-semibold text-sm">Nuevo curso</p>
          <input
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 80))}
            placeholder="Nombre del curso, p. ej. 4to"
            className={inputClass}
          />
          <label className="block space-y-1">
            <span className="text-sm text-ink-300">Estudiantes (opcional, uno por línea)</span>
            <textarea
              value={names}
              onChange={(e) => setNames(e.target.value)}
              rows={5}
              placeholder={'Ana Pérez\nLuis Gómez'}
              className="w-full px-3 py-2.5 rounded-lg bg-ink-850 border border-ink-700 text-ink-100 outline-none focus:border-sky-500 resize-y"
            />
          </label>
          <p className="text-xs text-ink-500">
            Sin lista, cada estudiante escribe su nombre al entregar. Con lista, lo elige: hace falta para repartir
            prácticas personales.
          </p>
          {error && <p className="text-danger-400 text-sm">{error}</p>}
          <button
            onClick={() => void create()}
            disabled={!name.trim() || busy}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold bg-ember-500 hover:bg-ember-400 text-on-accent disabled:bg-ink-800 disabled:text-ink-500"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Crear curso
          </button>
        </div>
      </div>
    </Modal>
  );
};

const CourseEditor: React.FC<{
  course: Course;
  busy: boolean;
  error: string;
  onBack: () => void;
  onRename: (name: string) => Promise<void>;
  onAdd: (names: string[]) => Promise<void>;
  onRemove: (studentId: string, name: string) => void;
  onRenameStudent: (studentId: string, name: string) => Promise<void>;
  onDelete: () => void;
}> = ({ course, busy, error, onBack, onRename, onAdd, onRemove, onRenameStudent, onDelete }) => {
  const [name, setName] = useState(course.name);
  const [names, setNames] = useState('');
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);

  return (
    <div className="space-y-5">
      <button onClick={onBack} className="flex items-center gap-1.5 text-ink-400 hover:text-ink-100 text-sm">
        <ArrowLeft className="w-4 h-4" /> Cursos
      </button>

      <div className="flex gap-2">
        <input value={name} onChange={(e) => setName(e.target.value.slice(0, 80))} className={inputClass} />
        <button
          onClick={() => void onRename(name)}
          disabled={busy || !name.trim() || name.trim() === course.name}
          className="px-4 rounded-lg font-semibold text-sm border border-ink-700 bg-ink-850 hover:bg-ink-800 disabled:opacity-40"
        >
          Renombrar
        </button>
      </div>

      <div className="space-y-2">
        <p className="font-semibold text-sm">
          Estudiantes <span className="text-ink-500 font-normal">({course.students.length})</span>
        </p>
        {course.students.length === 0 ? (
          <p className="text-ink-400 text-sm">Sin lista: cada estudiante escribirá su nombre al entregar.</p>
        ) : (
          <ul className="divide-y divide-ink-800 rounded-lg border border-ink-700">
            {course.students.map((s) => (
              <li key={s.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                {editing?.id === s.id ? (
                  <form
                    className="flex-1 flex gap-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      void onRenameStudent(s.id, editing.name).then(() => setEditing(null));
                    }}
                  >
                    <input
                      value={editing.name}
                      onChange={(e) => setEditing({ id: s.id, name: e.target.value.slice(0, 80) })}
                      autoFocus
                      className="flex-1 h-8 px-2 rounded bg-ink-850 border border-ink-700 outline-none focus:border-sky-500"
                    />
                    <button type="submit" aria-label="Guardar" className="p-1.5 text-success-400">
                      <Check className="w-4 h-4" />
                    </button>
                    <button type="button" aria-label="Cancelar" onClick={() => setEditing(null)} className="p-1.5 text-ink-400">
                      <X className="w-4 h-4" />
                    </button>
                  </form>
                ) : (
                  <>
                    <span className="flex-1 min-w-0 truncate">{s.name}</span>
                    <button
                      onClick={() => setEditing({ id: s.id, name: s.name })}
                      aria-label={`Renombrar a ${s.name}`}
                      className="p-1.5 rounded text-ink-400 hover:text-ink-100"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onRemove(s.id, s.name)}
                      aria-label={`Quitar a ${s.name}`}
                      className="p-1.5 rounded text-ink-400 hover:text-danger-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <label className="block space-y-1">
        <span className="text-sm text-ink-300">Añadir estudiantes (uno por línea)</span>
        <textarea
          value={names}
          onChange={(e) => setNames(e.target.value)}
          rows={3}
          className="w-full px-3 py-2.5 rounded-lg bg-ink-850 border border-ink-700 text-ink-100 outline-none focus:border-sky-500 resize-y"
        />
      </label>
      {error && <p className="text-danger-400 text-sm">{error}</p>}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => void onAdd(parseNames(names)).then(() => setNames(''))}
          disabled={busy || parseNames(names).length === 0}
          className="flex items-center gap-1.5 px-4 h-10 rounded-lg font-semibold text-sm bg-ember-500 hover:bg-ember-400 text-on-accent disabled:bg-ink-800 disabled:text-ink-500"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Añadir
        </button>
        <button
          onClick={onDelete}
          className="ml-auto flex items-center gap-1.5 px-3 h-10 rounded-lg text-sm font-medium border border-danger-500/40 text-danger-400 hover:bg-danger-500/10"
        >
          <Trash2 className="w-4 h-4" /> Borrar curso
        </button>
      </div>
    </div>
  );
};

/* ================================================================
 * Crear y editar
 * ================================================================ */

/** ms → valor de un <input type="datetime-local"> (hora local). */
function toLocalInput(ms: number | null): string {
  if (!ms) return '';
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const TaskFormModal: React.FC<{
  task?: TaskSummary;
  courses: Course[];
  defaultCourseId?: string | null;
  onManageCourses?: () => void;
  onClose: () => void;
  onSaved: (task: TaskSummary) => void;
}> = ({ task, courses, defaultCourseId, onManageCourses, onClose, onSaved }) => {
  const [title, setTitle] = useState(task?.title ?? '');
  const [instructions, setInstructions] = useState(task?.instructions ?? '');
  const [courseId, setCourseId] = useState(task ? task.courseId ?? '' : defaultCourseId ?? '');
  const [due, setDue] = useState(toLocalInput(task?.dueAt ?? null));
  const [acceptLate, setAcceptLate] = useState(task?.acceptLate ?? true);
  const [gradeScale, setGradeScale] = useState(task?.gradeScale ?? 100);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    if (task && courseId !== (task.courseId ?? '')) {
      if (!confirm('Al cambiar de curso se borran las prácticas personales ya subidas a esta tarea. ¿Seguir?')) return;
    }
    setBusy(true);
    setError('');
    const body = {
      title,
      instructions,
      courseId: courseId || null,
      dueAt: due ? new Date(due).getTime() : null,
      acceptLate,
      gradeScale,
    };
    try {
      let saved = task
        ? (await api.patch<{ task: TaskSummary }>(`/tasks/${task.id}`, body)).task
        : (await api.post<{ task: TaskSummary }>('/tasks', body)).task;
      if (file) saved = await taskApi.uploadAttachment(saved.id, file);
      onSaved(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar la tarea.');
      setBusy(false);
    }
  };

  return (
    <Modal title={task ? 'Editar tarea' : 'Nueva tarea'} onClose={onClose} wide>
      <div className="space-y-4">
        <label className="block space-y-1">
          <span className="text-sm text-ink-300">Título</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value.slice(0, 120))}
            placeholder="Ej.: Práctica de refuerzo, unidades 1 y 2"
            className={inputClass}
            autoFocus
          />
        </label>

        <label className="block space-y-1">
          <span className="flex items-center justify-between text-sm text-ink-300">
            Curso
            {onManageCourses && (
              <button type="button" onClick={onManageCourses} className="text-xs text-sky-400 hover:underline">
                Gestionar cursos
              </button>
            )}
          </span>
          <select value={courseId} onChange={(e) => setCourseId(e.target.value)} className={inputClass}>
            <option value="">Sin curso</option>
            {courses.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
                {c.students.length ? ` (${c.students.length} estudiantes)` : ''}
              </option>
            ))}
          </select>
        </label>

        <label className="block space-y-1">
          <span className="text-sm text-ink-300">Instrucciones</span>
          <textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value.slice(0, 5000))}
            rows={5}
            placeholder="Qué tienen que hacer, cómo se evalúa…"
            className="w-full px-3 py-2.5 rounded-lg bg-ink-850 border border-ink-700 text-ink-100 outline-none focus:border-sky-500 resize-y"
          />
        </label>

        {!task && (
          <label className="block space-y-1">
            <span className="text-sm text-ink-300">Archivo con el enunciado, igual para todos (opcional)</span>
            <input
              type="file"
              accept={TASK_FILE_ACCEPT}
              onChange={(e) => {
                const f = e.target.files?.[0];
                const problem = checkTaskFile(f);
                setError(problem ?? '');
                setFile(problem ? null : f ?? null);
              }}
              className="block w-full text-sm text-ink-300 file:mr-3 file:px-3 file:h-9 file:rounded-lg file:border file:border-ink-700 file:bg-ink-850 file:text-ink-100"
            />
          </label>
        )}

        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block space-y-1">
            <span className="text-sm text-ink-300">Fecha de entrega (opcional)</span>
            <input type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)} className={inputClass} />
          </label>
          <label className="block space-y-1">
            <span className="text-sm text-ink-300">Nota máxima</span>
            <input
              type="number"
              min={1}
              max={1000}
              value={gradeScale}
              onChange={(e) => setGradeScale(Math.max(0, Math.round(Number(e.target.value) || 0)))}
              className={inputClass}
            />
          </label>
        </div>

        {due && (
          <Toggle
            checked={acceptLate}
            onChange={setAcceptLate}
            label="Admitir entregas con retraso"
            hint="Pasada la fecha se puede seguir entregando, pero queda marcado «con retraso»."
          />
        )}

        {task && task.gradeScale !== gradeScale && (
          <p className="text-xs text-ink-500">Las notas ya puestas se pasarán a la nueva escala.</p>
        )}

        {error && <p className="text-danger-400 text-sm">{error}</p>}
        <button
          onClick={() => void save()}
          disabled={!title.trim() || busy}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold bg-ember-500 hover:bg-ember-400 text-on-accent disabled:bg-ink-800 disabled:text-ink-500"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : task ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {task ? 'Guardar' : 'Crear y obtener enlace'}
        </button>
      </div>
    </Modal>
  );
};

/* ================================================================
 * Detalle, prácticas y entregas
 * ================================================================ */

const TaskDetailView: React.FC<{
  taskId: string;
  courses: Course[];
  onCoursesChange: () => void;
  /** `replace`: la tarea ya no existe, su dirección no se queda en el historial. */
  onBack: (opts?: { replace?: boolean }) => void;
}> = ({ taskId, courses, onCoursesChange, onBack }) => {
  const [detail, setDetail] = useState<TaskDetail | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [editing, setEditing] = useState(false);
  const [grading, setGrading] = useState<TaskSubmissionSummary | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [uploading, setUploading] = useState(false);
  const attachInput = useRef<HTMLInputElement>(null);
  const bulkInput = useRef<HTMLInputElement>(null);
  const oneInput = useRef<HTMLInputElement>(null);
  const [oneFor, setOneFor] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setDetail(await api.get<TaskDetail>(`/tasks/${taskId}`));
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar la tarea.');
    }
  }, [taskId]);

  useEffect(() => {
    void load();
    // Las entregas van apareciendo solas mientras la página está abierta.
    const id = window.setInterval(() => void load(), 20_000);
    return () => window.clearInterval(id);
  }, [load]);

  const task = detail?.task ?? null;

  const patch = async (body: Record<string, unknown>) => {
    try {
      await api.patch(`/tasks/${taskId}`, body);
      void load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar.');
    }
  };

  const copy = async () => {
    if (!task) return;
    try {
      await navigator.clipboard.writeText(taskUrl(task.code));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* el navegador no deja copiar: el enlace está a la vista */
    }
  };

  const fail = (msg: string) => (err: unknown) => setError(err instanceof Error ? err.message : msg);

  const viewSubmission = (s: TaskSubmissionSummary) =>
    openFile(s.fileName ?? 'entrega.pdf', () => taskApi.submissionFile(taskId, s.id)).catch(fail('No se pudo abrir.'));

  const uploadAttachment = async (f: File | undefined) => {
    const problem = checkTaskFile(f);
    if (problem || !f) return setError(problem ?? '');
    setUploading(true);
    try {
      await taskApi.uploadAttachment(taskId, f);
      void load();
    } catch (err) {
      fail('No se pudo subir el archivo.')(err);
    } finally {
      setUploading(false);
    }
  };

  /**
   * Varias prácticas de golpe: cada archivo va al estudiante cuyo nombre lleva
   * («Practica - Ana Pérez.docx» → Ana Pérez). Los que no casan con nadie se
   * añaden al curso, previa confirmación.
   */
  const uploadMany = async (files: File[]) => {
    if (!detail || !task?.courseId) return;
    const bad = files.map((f) => [f, checkTaskFile(f)] as const).filter(([, p]) => p);
    if (bad.length) return setError(`${bad[0][0].name}: ${bad[0][1]}`);

    const roster = detail.roster.map((r) => r.student);
    const matchOf = (file: File) => {
      const key = nameKey(nameFromFile(file.name));
      return (
        roster.find((s) => nameKey(s.name) === key) ??
        // «Pieter» casa con «Pieter Pérez» y al revés, si no hay duda.
        (() => {
          const loose = roster.filter((s) => nameKey(s.name).includes(key) || key.includes(nameKey(s.name)));
          return loose.length === 1 ? loose[0] : undefined;
        })()
      );
    };

    const unmatched = files.filter((f) => !matchOf(f));
    if (unmatched.length) {
      const names = unmatched.map((f) => nameFromFile(f.name));
      if (
        !confirm(
          `Estos archivos no casan con nadie del curso:\n\n${names.join('\n')}\n\n¿Añadir esos nombres al curso y asignarles su archivo?`,
        )
      ) {
        return;
      }
      const { course } = await api.post<{ course: Course }>(`/courses/${task.courseId}/students`, { names });
      roster.splice(0, roster.length, ...course.students);
      onCoursesChange();
    }

    setUploading(true);
    setError('');
    let done = 0;
    try {
      for (const f of files) {
        const student = matchOf(f);
        if (!student) continue;
        const n = done + 1;
        setNotice(`Subiendo ${n} de ${files.length}…`);
        await taskApi.uploadMaterial(taskId, student.id, f, (p) =>
          setNotice(`Subiendo ${n} de ${files.length}… ${Math.round(p * 100)} %`),
        );
        done += 1;
      }
      setNotice(`${done} ${done === 1 ? 'práctica subida' : 'prácticas subidas'}.`);
    } catch (err) {
      fail('No se pudo subir.')(err);
      setNotice('');
    } finally {
      setUploading(false);
      void load();
    }
  };

  const uploadOne = async (studentId: string, f: File | undefined) => {
    const problem = checkTaskFile(f);
    if (problem || !f) return setError(problem ?? '');
    setUploading(true);
    try {
      await taskApi.uploadMaterial(taskId, studentId, f);
      void load();
    } catch (err) {
      fail('No se pudo subir.')(err);
    } finally {
      setUploading(false);
    }
  };

  const removeMaterial = async (row: TaskRosterRow) => {
    if (!confirm(`¿Quitar la práctica de ${row.student.name}?`)) return;
    await api.delete(`/tasks/${taskId}/materials/${row.student.id}`);
    void load();
  };

  const removeSubmission = async (s: TaskSubmissionSummary) => {
    const who = `${s.firstName} ${s.lastName}`.trim();
    if (!confirm(`¿Borrar la entrega de ${who}? No se puede deshacer.`)) return;
    await api.delete(`/tasks/${taskId}/submissions/${s.id}`);
    void load();
  };

  const removeTask = async () => {
    await api.delete(`/tasks/${taskId}`);
    onBack({ replace: true });
  };

  const exportCsv = () => {
    if (!detail || !task) return;
    const subs = [
      ...detail.roster.map((r) => ({ name: r.student.name, s: r.submission, r })),
      ...detail.submissions.map((s) => ({ name: `${s.lastName}, ${s.firstName}`, s, r: null })),
    ];
    const rows = [
      ['Estudiante', 'Práctica', 'Descargó su práctica', 'Estado', 'Entrega', 'Archivo', 'Nota', 'Nota máxima', 'Comentario'],
      ...subs.map(({ name, s, r }) => [
        name,
        r?.material?.name ?? '',
        r?.downloadedAt ? new Date(r.downloadedAt).toLocaleString('es') : r?.material ? 'No' : '',
        statusLabel(s),
        s?.submittedAt ? new Date(s.submittedAt).toLocaleString('es') : '',
        s?.fileName ?? '',
        s?.grade ?? '',
        task.gradeScale,
        s?.feedback ?? '',
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `tarea-${task.title.slice(0, 40)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (!detail || !task) {
    return (
      <div className="space-y-4">
        <BackButton onClick={() => onBack()} />
        {error ? <p className="text-danger-400">{error}</p> : <Loader2 className="w-6 h-6 animate-spin text-ink-400" />}
      </div>
    );
  }

  const hasRoster = detail.roster.length > 0;
  const submittedCount =
    detail.roster.filter((r) => r.submission?.submittedAt).length +
    detail.submissions.filter((s) => s.submittedAt).length;
  const materials = detail.roster.filter((r) => r.material).length;

  return (
    <div className="space-y-6">
      <BackButton onClick={() => onBack()} />

      <div className="surface-raised p-5 sm:p-6 space-y-4">
        <div>
          {task.courseName && (
            <p className="text-xs font-bold uppercase tracking-wider text-magenta-400">{task.courseName}</p>
          )}
          <h1 className="text-2xl font-display font-bold">{task.title}</h1>
          <p className="text-ink-400 text-sm mt-1">
            {task.dueAt ? `Entrega: ${formatDue(task.dueAt)}` : 'Sin fecha de entrega'}
            {task.dueAt ? (task.acceptLate ? ' · admite retrasos' : ' · sin retrasos') : ''} · nota sobre{' '}
            {task.gradeScale}
          </p>
        </div>

        <div className="flex items-center gap-2 rounded-lg border border-ink-700 bg-ink-850 px-3 py-2">
          <span className="flex-1 min-w-0 truncate font-mono text-xs text-ink-300">{taskUrl(task.code)}</span>
        </div>

        {task.instructions && (
          <p className="text-ink-200 text-sm whitespace-pre-wrap rounded-xl bg-ink-850 border border-ink-700 p-3">
            {task.instructions}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Paperclip className="w-4 h-4 text-ink-400" />
          {task.attachmentName ? (
            <>
              <button
                onClick={() =>
                  void openFile(task.attachmentName!, () => taskApi.attachment(task.code)).catch(
                    fail('No se pudo abrir el archivo.'),
                  )
                }
                className="text-sky-400 hover:underline truncate max-w-[16rem]"
              >
                {task.attachmentName}
              </button>
              <span className="text-ink-500">{formatBytes(task.attachmentSize)}</span>
              <button onClick={() => attachInput.current?.click()} className="text-ink-400 hover:text-ink-100">
                Cambiar
              </button>
              <button
                onClick={() => {
                  if (confirm('¿Quitar el archivo del enunciado?'))
                    void api.delete(`/tasks/${taskId}/attachment`).then(() => load());
                }}
                className="text-ink-400 hover:text-danger-400"
              >
                Quitar
              </button>
            </>
          ) : (
            <button onClick={() => attachInput.current?.click()} className="text-ink-300 hover:text-ink-100">
              Adjuntar un enunciado igual para todos
            </button>
          )}
          <input
            ref={attachInput}
            type="file"
            accept={TASK_FILE_ACCEPT}
            className="hidden"
            onChange={(e) => {
              void uploadAttachment(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <SmallButton onClick={() => void copy()} icon={copied ? <Check /> : <Copy />}>
            Copiar enlace
          </SmallButton>
          <SmallButton onClick={() => setShowQr(true)} icon={<QrCode />}>
            Código QR
          </SmallButton>
          <SmallButton onClick={() => void patch({ isOpen: !task.isOpen })} icon={task.isOpen ? <Lock /> : <Unlock />}>
            {task.isOpen ? 'Cerrar entregas' : 'Volver a abrir'}
          </SmallButton>
          <SmallButton onClick={() => setEditing(true)} icon={<Settings />}>
            Editar
          </SmallButton>
          <SmallButton onClick={() => setConfirmDelete(true)} icon={<Trash2 />} danger>
            Borrar tarea
          </SmallButton>
        </div>
        <p className="text-xs text-ink-500">
          Solo se entra con este enlace o su código QR.{' '}
          {task.isOpen
            ? 'Abierta: quien lo tenga puede entregar.'
            : 'Cerrada: nadie puede entregar ni cambiar su entrega. Siguen pudiendo ver su nota y su práctica.'}
        </p>
        {error && <p className="text-danger-400 text-sm">{error}</p>}
      </div>

      {task.courseId && (
        <section className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-xl font-bold flex-1">
              {hasRoster ? 'Estudiantes' : 'Entregas'}{' '}
              <span className="text-ink-500 text-base font-normal">
                {hasRoster
                  ? `(${submittedCount} de ${detail.roster.length} entregaron · ${materials} prácticas)`
                  : `(${submittedCount})`}
              </span>
            </h2>
            {hasRoster && (
              <SmallButton onClick={() => bulkInput.current?.click()} icon={<Upload />} disabled={uploading}>
                Subir prácticas
              </SmallButton>
            )}
            <SmallButton onClick={() => void load()} icon={<RefreshCw />}>
              Actualizar
            </SmallButton>
            <SmallButton onClick={exportCsv} icon={<Download />}>
              CSV
            </SmallButton>
            <input
              ref={bulkInput}
              type="file"
              multiple
              accept={TASK_FILE_ACCEPT}
              className="hidden"
              onChange={(e) => {
                void uploadMany([...(e.target.files ?? [])]);
                e.target.value = '';
              }}
            />
          </div>
          {hasRoster && (
            <p className="text-xs text-ink-500">
              «Subir prácticas»: elige todos los archivos a la vez. Cada uno va al estudiante que lleva en el nombre
              («Practica - Ana Pérez.docx»). Cada estudiante solo ve y descarga la suya.
            </p>
          )}
          {notice && <p className="text-sky-400 text-sm">{notice}</p>}
        </section>
      )}

      {hasRoster && (
        <div className="surface overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-ink-500 text-xs uppercase tracking-wider">
              <tr className="border-b border-ink-700">
                <th className="text-left p-3">Estudiante</th>
                <th className="text-left p-3">Su práctica</th>
                <th className="text-left p-3">Entrega</th>
                <th className="text-right p-3">Nota</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {detail.roster.map((r) => {
                const s = r.submission;
                return (
                  <tr key={r.student.id} className="border-b border-ink-800 last:border-0 align-top">
                    <td className="p-3 font-semibold">{r.student.name}</td>
                    <td className="p-3">
                      {r.material ? (
                        <>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() =>
                                void openFile(r.material!.name, () => taskApi.material(taskId, r.student.id)).catch(
                                  fail('No se pudo abrir.'),
                                )
                              }
                              className="text-sky-400 hover:underline text-left break-all"
                            >
                              {r.material.name}
                            </button>
                            <button
                              onClick={() => void removeMaterial(r)}
                              aria-label="Quitar práctica"
                              className="p-1 text-ink-500 hover:text-danger-400 shrink-0"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <span className={`block text-xs mt-0.5 ${r.downloadedAt ? 'text-success-400' : 'text-ember-300'}`}>
                            {r.downloadedAt
                              ? `La descargó el ${new Date(r.downloadedAt).toLocaleString('es')}${r.downloads > 1 ? ` (${r.downloads} veces)` : ''}`
                              : 'No la ha descargado'}
                          </span>
                        </>
                      ) : (
                        <button
                          onClick={() => {
                            setOneFor(r.student.id);
                            oneInput.current?.click();
                          }}
                          className="flex items-center gap-1 text-ink-400 hover:text-ink-100"
                        >
                          <FileUp className="w-4 h-4" /> Subir
                        </button>
                      )}
                    </td>
                    <td className="p-3">
                      <SubmissionCell s={s} onView={viewSubmission} />
                    </td>
                    <td className="p-3 text-right font-bold tabular-nums whitespace-nowrap">
                      {s?.grade != null ? `${formatGrade(s.grade)} / ${task.gradeScale}` : '—'}
                    </td>
                    <td className="p-3">
                      {s && <RowActions s={s} onGrade={setGrading} onDelete={removeSubmission} />}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <input
            ref={oneInput}
            type="file"
            accept={TASK_FILE_ACCEPT}
            className="hidden"
            onChange={(e) => {
              if (oneFor) void uploadOne(oneFor, e.target.files?.[0]);
              e.target.value = '';
            }}
          />
        </div>
      )}

      {(!hasRoster || detail.submissions.length > 0) && (
        <section className="space-y-3">
          {!task.courseId && (
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-xl font-bold flex-1">
                Entregas <span className="text-ink-500 text-base font-normal">({submittedCount})</span>
              </h2>
              <SmallButton onClick={() => void load()} icon={<RefreshCw />}>
                Actualizar
              </SmallButton>
              <SmallButton onClick={exportCsv} icon={<Download />} disabled={detail.submissions.length === 0}>
                CSV
              </SmallButton>
            </div>
          )}
          {hasRoster && <h3 className="text-sm font-bold uppercase tracking-wider text-ink-400">Fuera de la lista</h3>}
          {detail.submissions.length === 0 ? (
            <p className="surface p-6 text-ink-400 text-sm text-center">
              Todavía nadie ha entregado. Comparte el enlace o el código QR.
            </p>
          ) : (
            <div className="surface overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-ink-500 text-xs uppercase tracking-wider">
                  <tr className="border-b border-ink-700">
                    <th className="text-left p-3">Estudiante</th>
                    <th className="text-left p-3">Entrega</th>
                    <th className="text-right p-3">Nota</th>
                    <th className="p-3" />
                  </tr>
                </thead>
                <tbody>
                  {detail.submissions.map((s) => (
                    <tr key={s.id} className="border-b border-ink-800 last:border-0 align-top">
                      <td className="p-3 font-semibold">
                        {s.lastName ? `${s.lastName}, ${s.firstName}` : s.firstName}
                      </td>
                      <td className="p-3">
                        <SubmissionCell s={s} onView={viewSubmission} />
                      </td>
                      <td className="p-3 text-right font-bold tabular-nums whitespace-nowrap">
                        {s.grade !== null ? `${formatGrade(s.grade)} / ${task.gradeScale}` : '—'}
                      </td>
                      <td className="p-3">
                        <RowActions s={s} onGrade={setGrading} onDelete={removeSubmission} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <p className="text-xs text-ink-500">
        Las entregas y las notas se guardan hasta que tú las borres. Quien ya está calificado no puede cambiar su
        archivo; si borras la nota, vuelve a poder.
      </p>

      {showQr && <QrModal url={taskUrl(task.code)} title={task.title} onClose={() => setShowQr(false)} />}

      {editing && (
        <TaskFormModal
          task={task}
          courses={courses}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            void load();
          }}
        />
      )}

      {grading && (
        <GradeModal
          taskId={taskId}
          gradeScale={task.gradeScale}
          submission={grading}
          onView={() => void viewSubmission(grading)}
          onClose={() => setGrading(null)}
          onSaved={() => {
            setGrading(null);
            void load();
          }}
        />
      )}

      {confirmDelete && (
        <Modal title="¿Borrar la tarea?" onClose={() => setConfirmDelete(false)}>
          <p className="text-ink-300 text-sm">
            Se borrarán la tarea, las prácticas subidas y <strong>todas sus entregas y notas</strong> ({submittedCount}
            ). No se puede deshacer. Si solo quieres que nadie más entregue, mejor ciérrala.
          </p>
          <div className="flex gap-3 mt-5">
            <button
              onClick={() => setConfirmDelete(false)}
              className="flex-1 px-4 py-3 rounded-xl font-semibold bg-ink-800 hover:bg-ink-700 border border-ink-600 text-ink-100"
            >
              Cancelar
            </button>
            <button
              onClick={() => void removeTask()}
              className="flex-1 px-4 py-3 rounded-xl font-bold bg-danger-500 hover:bg-danger-400 text-white"
            >
              Borrar todo
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

function statusLabel(s: TaskSubmissionSummary | null): string {
  if (!s?.submittedAt) return 'Sin entregar';
  if (s.gradedAt) return s.late ? 'Calificada (con retraso)' : 'Calificada';
  return s.late ? 'Entregada con retraso' : 'Entregada';
}

const SubmissionCell: React.FC<{
  s: TaskSubmissionSummary | null;
  onView: (s: TaskSubmissionSummary) => void;
}> = ({ s, onView }) => (
  <div>
    <span className={!s?.submittedAt ? 'text-ink-500' : s.late ? 'text-ember-300' : 'text-success-400'}>
      {statusLabel(s)}
    </span>
    {s?.fileName && (
      <button
        onClick={() => onView(s)}
        className="block text-sky-400 hover:underline text-left break-all text-xs mt-0.5"
        title="Abrir"
      >
        {s.fileName}
      </button>
    )}
    {s?.submittedAt && (
      <span className="block text-ink-500 text-xs">{new Date(s.submittedAt).toLocaleString('es')}</span>
    )}
    {s?.note && <span className="block text-ink-400 text-xs mt-0.5 italic">«{s.note}»</span>}
  </div>
);

const RowActions: React.FC<{
  s: TaskSubmissionSummary;
  onGrade: (s: TaskSubmissionSummary) => void;
  onDelete: (s: TaskSubmissionSummary) => void;
}> = ({ s, onGrade, onDelete }) => (
  <div className="flex justify-end gap-1">
    <button
      onClick={() => onGrade(s)}
      disabled={!s.submittedAt}
      aria-label="Calificar"
      title="Calificar y comentar"
      className="p-2 rounded-lg text-ink-400 hover:text-ink-100 hover:bg-ink-800 disabled:opacity-30"
    >
      <MessageSquare className="w-4 h-4" />
    </button>
    <button
      onClick={() => onDelete(s)}
      aria-label="Borrar entrega"
      title="Borrar la entrega"
      className="p-2 rounded-lg text-ink-400 hover:text-danger-400 hover:bg-ink-800"
    >
      <Trash2 className="w-4 h-4" />
    </button>
  </div>
);

/** El QR lleva el enlace: escanearlo es lo mismo que abrirlo. */
const QrModal: React.FC<{ url: string; title: string; onClose: () => void }> = ({ url, title, onClose }) => {
  const [src, setSrc] = useState('');
  useEffect(() => {
    void QRCode.toDataURL(url, { width: 640, margin: 2, errorCorrectionLevel: 'M' }).then(setSrc);
  }, [url]);
  return (
    <Modal title="Código QR de la tarea" onClose={onClose}>
      <div className="space-y-4 text-center">
        <p className="text-sm text-ink-300">{title}</p>
        <div className="bg-white rounded-xl p-3 inline-block">
          {src ? (
            <img src={src} alt="Código QR con el enlace de la tarea" className="w-64 h-64" />
          ) : (
            <Loader2 className="w-6 h-6 animate-spin text-ink-400 m-28" />
          )}
        </div>
        <p className="text-xs text-ink-500">
          Escanearlo abre la tarea, igual que el enlace. Proyéctalo en clase o imprímelo.
        </p>
        <a
          href={src}
          download={`QR - ${title.slice(0, 60)}.png`}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold bg-ember-500 hover:bg-ember-400 text-on-accent"
        >
          <Download className="w-4 h-4" /> Descargar imagen
        </a>
      </div>
    </Modal>
  );
};

const GradeModal: React.FC<{
  taskId: string;
  gradeScale: number;
  submission: TaskSubmissionSummary;
  onView: () => void;
  onClose: () => void;
  onSaved: () => void;
}> = ({ taskId, gradeScale, submission, onView, onClose, onSaved }) => {
  const [grade, setGrade] = useState(submission.grade === null ? '' : String(submission.grade));
  const [feedback, setFeedback] = useState(submission.feedback);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      await api.patch(`/tasks/${taskId}/submissions/${submission.id}`, {
        grade: grade.trim() === '' ? null : Number(grade.replace(',', '.')),
        feedback,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar.');
      setBusy(false);
    }
  };

  return (
    <Modal title={`${submission.firstName} ${submission.lastName}`.trim()} onClose={onClose}>
      <div className="space-y-4">
        <button
          onClick={onView}
          className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border border-ink-700 bg-ink-850 hover:bg-ink-800 text-sm text-left"
        >
          <FileUp className="w-4 h-4 text-sky-400 shrink-0" />
          <span className="flex-1 min-w-0 truncate">{submission.fileName}</span>
          <span className="text-ink-500 text-xs">Abrir</span>
        </button>
        {submission.note && <p className="text-sm text-ink-300 italic">«{submission.note}»</p>}

        <label className="block space-y-1">
          <span className="text-sm text-ink-300">Nota (sobre {gradeScale})</span>
          <input
            value={grade}
            onChange={(e) => setGrade(e.target.value.replace(/[^\d.,]/g, '').slice(0, 7))}
            inputMode="decimal"
            placeholder="Sin calificar"
            className={inputClass}
            autoFocus
          />
        </label>
        <label className="block space-y-1">
          <span className="text-sm text-ink-300">Comentario para el estudiante</span>
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value.slice(0, 3000))}
            rows={4}
            className="w-full px-3 py-2.5 rounded-lg bg-ink-850 border border-ink-700 text-ink-100 outline-none focus:border-sky-500 resize-y"
          />
        </label>
        <p className="text-xs text-ink-500">
          Lo verá al volver a abrir la tarea. Con nota o comentario, ya no podrá cambiar su archivo.
        </p>
        {error && <p className="text-danger-400 text-sm">{error}</p>}
        <button
          onClick={() => void save()}
          disabled={busy}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold bg-ember-500 hover:bg-ember-400 text-on-accent disabled:bg-ink-800 disabled:text-ink-500"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          Guardar
        </button>
      </div>
    </Modal>
  );
};

const BackButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button onClick={onClick} className="flex items-center gap-1.5 text-ink-400 hover:text-ink-100 text-sm">
    <ArrowLeft className="w-4 h-4" /> Mis tareas
  </button>
);
